import React from "react";
import { Sparkles, Gift, Award, TrendingUp, Zap, ShieldCheck, CheckCircle2, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";

const TIERS = [
  { name: "Bronze", spend: "₹0 – ₹50,000", points: "1× on every ₹100", perks: ["Standard shipping", "Email support", "Early sale access"], color: "from-amber-500 to-amber-700", icon: Award },
  { name: "Silver", spend: "₹50,001 – ₹2L", points: "2× on every ₹100", perks: ["Free 3-day shipping", "Priority support", "Exclusive brand deals", "Free birthday gift"], color: "from-slate-400 to-slate-600", icon: Sparkles },
  { name: "Gold", spend: "₹2L – ₹5L", points: "3× on every ₹100", perks: ["Free same-day dispatch", "Dedicated account manager", "Early access to new launches", "Invite to Dyntra Events", "Free product samples"], color: "from-yellow-500 to-yellow-700", icon: TrendingUp, popular: true },
  { name: "Platinum", spend: "₹5L+", points: "5× on every ₹100 + bonus", perks: ["24/7 white-glove support", "Custom bulk pricing", "Free installation on equipment", "VIP Dyntra Events access", "Complimentary Dr. Dyntra Pro credits"], color: "from-purple-600 to-purple-900", icon: ShieldCheck },
];

const EARN_WAYS = [
  { icon: Gift, title: "Every Purchase", desc: "Earn 1–5× points per ₹100 based on your tier." },
  { icon: Sparkles, title: "Refer a Dentist", desc: "500 points when they place their first order." },
  { icon: Award, title: "Attend Events", desc: "100 points per Dyntra event or webinar you attend." },
  { icon: TrendingUp, title: "Write a Review", desc: "50 points for every verified product review." },
];

export default function Rewards() {
  return (
    <main className="pb-16">
      {/* Hero */}
      <section className="dyntra-container mt-6">
        <div className="rounded-3xl overflow-hidden relative" style={{ background: "linear-gradient(135deg, #4B2AAD 0%, #6B4CD9 50%, #FFB020 130%)" }}>
          <div className="relative px-8 sm:px-14 py-14 md:py-20 max-w-2xl text-white">
            <div className="dyntra-chip !bg-white/15 !text-white mb-3"><Sparkles className="h-3 w-3" /> Dyntra Rewards</div>
            <h1 data-testid="rewards-title" className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight">Every order earns.<br />Every reward matters.</h1>
            <p className="mt-3 text-white/90 text-base max-w-md">Turn your practice's purchases into rewards, VIP perks and exclusive event access.</p>
            <div className="flex flex-wrap items-center gap-3 mt-6">
              <Link to="/shop" className="inline-flex items-center gap-2 rounded-xl bg-white text-[#4B2AAD] px-5 py-2.5 text-sm font-bold hover:bg-white/90 shadow-lg">Start Earning <ArrowRight className="h-4 w-4" /></Link>
              <a href="#tiers" className="inline-flex items-center gap-2 rounded-xl border border-white/30 bg-white/10 backdrop-blur px-5 py-2.5 text-sm font-bold text-white hover:bg-white/20">See Tiers</a>
            </div>
          </div>
        </div>
      </section>

      {/* Ways to earn */}
      <section className="dyntra-container mt-14">
        <div className="text-center mb-8">
          <h2 className="font-display text-2xl sm:text-3xl font-bold text-gray-900">4 Ways to Earn Points</h2>
          <p className="text-sm text-gray-500 mt-1">Points never expire. Redeem on any order.</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {EARN_WAYS.map((w) => (
            <div key={w.title} data-testid={`earn-${w.title.replace(/\s+/g,"-").toLowerCase()}`} className="dyntra-card p-6 hover:translate-y-0">
              <div className="h-12 w-12 rounded-xl bg-[#F3EFFC] flex items-center justify-center mb-3">
                <w.icon className="h-6 w-6 text-[#4B2AAD]" />
              </div>
              <h3 className="font-display text-lg font-bold text-gray-900">{w.title}</h3>
              <p className="text-sm text-gray-500 mt-1">{w.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Tiers */}
      <section id="tiers" className="dyntra-container mt-14">
        <div className="text-center mb-8">
          <h2 className="font-display text-2xl sm:text-3xl font-bold text-gray-900">Membership Tiers</h2>
          <p className="text-sm text-gray-500 mt-1">Unlock bigger perks as you grow.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {TIERS.map((t) => (
            <div key={t.name} data-testid={`tier-${t.name.toLowerCase()}`} className={`relative dyntra-card p-6 hover:translate-y-[-4px] ${t.popular ? "border-[#4B2AAD]/30 ring-2 ring-[#4B2AAD]/10" : ""}`}>
              {t.popular && <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-[#4B2AAD] text-white text-[10px] font-bold px-3 py-1 uppercase tracking-wider">Most Popular</span>}
              <div className={`h-14 w-14 rounded-2xl bg-gradient-to-br ${t.color} flex items-center justify-center mb-4 shadow-lg`}>
                <t.icon className="h-6 w-6 text-white" />
              </div>
              <h3 className="font-display text-xl font-extrabold text-gray-900">{t.name}</h3>
              <p className="text-xs text-gray-500 mt-0.5">{t.spend} annual spend</p>
              <div className="mt-3 dyntra-chip"><Zap className="h-3 w-3" /> {t.points}</div>
              <ul className="mt-4 space-y-1.5">
                {t.perks.map((p) => (
                  <li key={p} className="flex items-start gap-2 text-sm text-gray-600">
                    <CheckCircle2 className="h-4 w-4 text-emerald-500 shrink-0 mt-0.5" /> {p}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="dyntra-container mt-14">
        <div className="rounded-3xl bg-[#4B2AAD] text-white p-10 md:p-14 flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <h2 className="font-display text-2xl sm:text-3xl font-bold">Start earning on your next order</h2>
            <p className="text-white/85 text-sm mt-1">Sign up in 30 seconds. Auto-enrolled to Bronze.</p>
          </div>
          <Link to="/shop" className="inline-flex items-center gap-2 rounded-xl bg-white text-[#4B2AAD] px-5 py-2.5 text-sm font-bold hover:bg-white/90 shadow-lg shrink-0">Shop Now <ArrowRight className="h-4 w-4" /></Link>
        </div>
      </section>
    </main>
  );
}
